import * as Sentry from "@sentry/nextjs";
import { GetServerSideProps, NextPage } from "next";
import { NextSeo } from "next-seo";
import React from "react";
import { useIntl } from "react-intl";

import {
  PRODUCT_VARIANT_MAX_COUNT,
  SUB_PRODUCT_MAX_COUNT,
  SUB_PRODUCT_VARIANT_MAX_COUNT,
} from "../../../../../config";
import {
  addApolloState,
  initializeApollo,
} from "../../../../apollo/apollo-client";
import ErrorPage from "../../../../components/ErrorPage";
import Product from "../../../../components/ecommerce/Product";
import Layout from "../../../../components/layouts/Default";
import {
  combineContainerAndSubProducts,
  getChildTagPrefixFromParent,
  getProductSKUFromTags,
  isChildTagPrefix,
  ProductTagPrefix,
  PRODUCT_TAG_SEPARATOR,
} from "../../../../ecommerce/product";
import {
  SubProductsData,
  SubProductsVariables,
  GET_SUB_PRODUCTS,
  GET_PRODUCT_BY_HANDLE,
} from "../../../../graphql/product";
import {
  UseProductData,
  UseProductVariables,
} from "../../../../hooks/useProduct";
import { getNextSeoConfigForProductPage } from "../../../../seo/seo";
import { Product as ShopifyProduct } from "../../../../types/shopify";

interface ProductPageProps {
  product?: ShopifyProduct;
}

const ProductPage: NextPage<ProductPageProps> = ({ product }) => {
  const { locale } = useIntl();

  if (!product) {
    return <ErrorPage />;
  }

  return (
    <Layout>
      <NextSeo {...getNextSeoConfigForProductPage(product, locale)} />
      <Product product={product} />
    </Layout>
  );
};

type URLParams = {
  locale: string;
  handle: string;
};

const notFound = {
  props: {
    notFound: true,
  },
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  try {
    const { locale, handle } = params as URLParams;
    const apolloClient = initializeApollo(locale);

    const { data, errors } = await apolloClient.query<
      UseProductData,
      UseProductVariables
    >({
      fetchPolicy: "no-cache",
      query: GET_PRODUCT_BY_HANDLE,
      variables: {
        handle,
        variantCount: PRODUCT_VARIANT_MAX_COUNT,
      },
    });

    errors?.forEach(error => {
      Sentry.captureException(error);
    });

    const product = data.productByHandle;

    const skuData = getProductSKUFromTags(product.tags);
    if (!product || (skuData && isChildTagPrefix(skuData.type))) {
      return notFound;
    }

    if (skuData && !isChildTagPrefix(skuData.type)) {
      const subPrefix = getChildTagPrefixFromParent(skuData.type);
      const { data, errors } = await apolloClient.query<
        SubProductsData,
        SubProductsVariables
      >({
        query: GET_SUB_PRODUCTS,
        variables: {
          productMaxCount: SUB_PRODUCT_MAX_COUNT,
          variantMaxCount: SUB_PRODUCT_VARIANT_MAX_COUNT,
          query: `tag:${subPrefix}\\${PRODUCT_TAG_SEPARATOR}${skuData.sku}`,
        },
      });

      errors?.forEach(error => {
        Sentry.captureException(error);
      });

      const combinedProduct = combineContainerAndSubProducts({
        containerProduct: product,
        subProducts: data.products.edges.map(edge => edge.node),
        includeContainer:
          skuData.type === ProductTagPrefix.COMPOSITE_PARENT_PRODUCT,
      });

      return addApolloState(apolloClient, {
        props: { product: combinedProduct },
      });
    }

    return addApolloState(apolloClient, {
      props: {
        product,
      },
    });
  } catch (error) {
    console.log(error);
    return notFound;
  }
};

export default ProductPage;
